/**
 * Bundle builder: step 1 (single chain) → step 2 (charms with qty UI).
 * Cart add merges quantities per variant ID (Shopify native line behavior).
 */
import {
  parseJsonScript,
  loadProducts,
  normalizeProductId,
  variantFeaturedSrc,
  formatMoney,
  cartAdd,
  buildBundleCartItems,
  uuid,
  variantAvailable,
  BUNDLE_ATC_LOADING_DOTS_HTML,
  stripHtmlForLength,
  truncatePlainToWords,
  QUICK_VIEW_DESC_WORD_LIMIT,
  detectMetalFromVariantTitle,
} from '@bundle-builder/utils';
import { bindBundleDragScroll } from '@bundle-builder/drag-scroll';
import {
  hydrateBundleGrids,
  refreshGridsBundleState,
  showMoreGrid,
  enrichProduct,
  applyStep2MetalFilter,
} from '@bundle-builder/grid';
import { renderBundleSidebar, renderBundlePromo } from '@bundle-builder/sidebar';
import {
  ensureQuickViewEls,
  openQuickView,
  closeQuickView,
  renderQuickModalActions,
  syncCardVariantFromModal,
} from '@bundle-builder/quick-view';

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.cfg = {};
        this.productsByBlock = new Map();
        this.chain = null;
        this.charms = [];
        this.uiStep = 1;
        this._progressStep3AfterAtc = false;
        this.selectedVariantByProduct = new Map();
        /** Metal token derived from chain variant title; filters step 2 variant options. */
        this.metalFilter = '';
        this._showMoreExpanded = new Set();
        this._initialVisible = 6;
        this._qvDescFullHtml = '';
        this._qvMainScrollCleanup = null;
        this._asidePortalPlaceholder = null;
        this._mobileAsideScrim = null;
        this._bundleAsideScrollY = null;
        this._onBundleViewportResize = null;
        this._bottomBanners = [];
      }

      connectedCallback() {
        void this._init();
      }

      async _init() {
        this.cfg = parseJsonScript(this, 'script[data-bundle-settings]');
        this._initialVisible = parseInt(this.getAttribute('data-initial-visible'), 10) || 6;
        window.bundleBuilderMoneyFormat = this.cfg.money_format;
        window.bundleBuilderCurrency = this.getAttribute('data-currency') || 'USD';
        this.productsByBlock = loadProducts(this);
        this._cacheEls();
        this._bind();
        await hydrateBundleGrids(this);
        this._renderProgress();
        renderBundleSidebar(this);
        this._updateATC();
        this._bindMobileAside();
        this._bindTabsDragScroll();
        this._updateBottomBanner();
        this._onBundleViewportResize = () => {
          if (document.documentElement.classList.contains('bundle-builder-mobile-aside-open')) {
            this._syncBundleMobileOverlayTop();
          }
        };
        window.addEventListener('resize', this._onBundleViewportResize);
        window.visualViewport?.addEventListener('resize', this._onBundleViewportResize);
      }

      disconnectedCallback() {
        closeQuickView(this);
        this._progressBtn?.removeEventListener('click', this._onJumpStep1);
        this._linesEl?.removeEventListener('click', this._onBundleLinesClick);
        this._asideToggleBtn?.removeEventListener('click', this._onAsideToggle);
        this._asideHead?.removeEventListener('click', this._onAsideHeadClick);
        this._mql?.removeEventListener('change', this._onMql);
        this._tabsDragCleanups?.forEach((fn) => fn());
        this._tabsDragCleanups = [];
        document.documentElement.classList.remove('bundle-builder-mobile-aside-open');
        document.documentElement.style.removeProperty('--bundle-mobile-overlay-top');
        if (this._onBundleViewportResize) {
          window.removeEventListener('resize', this._onBundleViewportResize);
          window.visualViewport?.removeEventListener('resize', this._onBundleViewportResize);
        }
        this._teardownMobileAsidePortal();
        if (this._bundleAsideScrollY != null) {
          const y = this._bundleAsideScrollY;
          this._bundleAsideScrollY = null;
          window.scrollTo({ top: y, behavior: 'auto' });
        }
      }

      _cacheEls() {
        this._panels1 = this.querySelector('.bundle-builder__panels--1');
        this._panels2 = this.querySelector('.bundle-builder__panels--2');
        this._head1 = this.querySelector('.bundle-builder__step-heading--1');
        this._head2 = this.querySelector('.bundle-builder__step-heading--2');
        this._linesEl = this.querySelector('[data-bundle-lines]');
        this._subtotalEl = this.querySelector('[data-bundle-subtotal]');
        this._asideSubtotalRow = this.querySelector('[data-bundle-subtotal-row]');
        this._countEl = this.querySelector('[data-bundle-count]');
        this._promoEl = this.querySelector('[data-bundle-promo]');
        this._atcBtn = this.querySelector('[data-bundle-atc]');
        this._progressNav = this.querySelector('.bundle-builder__progress');
        this._progressBtn = this.querySelector('[data-progress-jump="1"]');
        this._mainEl = this.querySelector('.bundle-builder__main');
        this._asideEl = this.querySelector('.bundle-builder__aside');
        this._asideHead = this.querySelector('.bundle-builder__aside-head');
        this._asideToggleBtn = this.querySelector('[data-bundle-aside-toggle]');
        this._asideToggleText = this.querySelector('[data-aside-toggle-text]');
        this._mql = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
        this._modal = null;
        ensureQuickViewEls(this);
        this._qvEscapeHandler = null;
        this._bottomBanners = Array.from(
          this.parentElement?.querySelectorAll('[data-bundle-bottom-banner]') ?? []
        );
      }

      _updateBottomBanner() {
        const active = this.chain ? '2' : '1';
        this._bottomBanners.forEach((el) => {
          el.setAttribute('data-bundle-banner-active', active);
        });
      }

      _bind() {
        ensureQuickViewEls(this);

        this._onJumpStep1 = () => this._jumpToStep1();
        this._progressBtn?.addEventListener('click', this._onJumpStep1);

        this.querySelectorAll('.bundle-builder__tab').forEach((tab) => {
          tab.addEventListener('click', () => this._activateTab(tab));
        });

        this.querySelectorAll('[data-show-more]').forEach((btn) => {
          btn.addEventListener('click', () => {
            showMoreGrid(this, btn.getAttribute('data-show-more'));
          });
        });

        this._atcBtn?.addEventListener('click', () => this._addBundleToCart());

        this._onBundleLinesClick = (e) => {
          const btn = e.target.closest('[data-remove-line]');
          if (!btn || !this._linesEl?.contains(btn)) return;
          e.preventDefault();
          const id = btn.getAttribute('data-remove-line');
          if (id) this._removeLine(id);
        };
        this._linesEl?.addEventListener('click', this._onBundleLinesClick);

        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-bundle-quick-view]');
          if (!btn || btn.closest('bundle-builder') !== this) return;
          const card = btn.closest('.bundle-builder__card');
          if (!card) return;
          e.preventDefault();
          const grid = card.closest('[data-bundle-grid]');
          if (!grid) return;
          const key = grid.getAttribute('data-bundle-grid');
          if (!key) return;
          const blockId = key.replace(/^step[12]-/, '');
          const step = key.startsWith('step1') ? 1 : 2;
          let product = this._productForCard(card);
          if (!product) {
            this.productsByBlock = loadProducts(this);
            product = this._productForCard(card);
          }
          if (!product) return;
          void openQuickView(this, product, step, card);
        });

        this._modal?.addEventListener('click', (e) => {
          if (e.target.closest('[data-bundle-quick-modal-close]')) {
            e.preventDefault();
            closeQuickView(this);
            return;
          }
          const add1 = e.target.closest('[data-qv-add-step1]');
          const add2 = e.target.closest('[data-qv-add-step2]');
          const qinc = e.target.closest('[data-qv-qty-inc]');
          const qdec = e.target.closest('[data-qv-qty-dec]');
          const ctx = this._qvContext;
          if (!ctx) return;
          const vid = parseInt(this._qvSelect?.value || '0', 10);
          if (add1) this._addChain(ctx.product, vid);
          if (add2) this._addCharm(ctx.product, vid);
          if (qinc) this._addCharm(ctx.product, vid);
          if (qdec) this._removeCharmVariant(vid);
        });

        this._qvReadMore?.addEventListener('click', () => {
          const desc = this._qvDesc;
          const btn = this._qvReadMore;
          const full = this._qvDescFullHtml || '';
          if (!desc || !btn) return;
          const collapsed = desc.classList.contains('is-desc-collapsed');
          if (collapsed) {
            desc.innerHTML = full;
            desc.classList.remove('is-desc-collapsed');
            btn.textContent = this.cfg.quick_view_show_less || 'Show less';
          } else {
            const plain = stripHtmlForLength(full);
            const { text } = truncatePlainToWords(plain, QUICK_VIEW_DESC_WORD_LIMIT);
            desc.textContent = text;
            desc.classList.add('is-desc-collapsed');
            btn.textContent = this.cfg.quick_view_read_more || 'Read more';
          }
        });

        this._qvSelect?.addEventListener('change', () => {
          syncCardVariantFromModal(this);
          renderQuickModalActions(this);
        });
      }

      _bindTabsDragScroll() {
        this._tabsDragCleanups = [];
        bindBundleDragScroll(this, this._tabsDragCleanups);
      }

      _bindMobileAside() {
        this._onAsideToggle = () => this._onAsideToggleClick();
        this._onAsideHeadClick = (e) => {
          if (this._mql?.matches) return;
          if (this.classList.contains('is-mobile-aside-open')) return;
          if (e.target.closest('[data-bundle-aside-toggle]')) return;
          this._setMobileAsideOpen(true);
        };
        this._onMql = () => {
          if (this._mql?.matches) this._setMobileAsideOpen(false);
        };
        this._asideToggleBtn?.addEventListener('click', this._onAsideToggle);
        this._asideHead?.addEventListener('click', this._onAsideHeadClick);
        this._mql?.addEventListener('change', this._onMql);
      }

      _onAsideToggleClick() {
        if (this._mql?.matches) return;
        this._setMobileAsideOpen(!this.classList.contains('is-mobile-aside-open'));
      }

      _setMobileAsideOpen(open) {
        const mobile = !this._mql?.matches;

        this.classList.toggle('is-mobile-aside-open', open);
        this._asideToggleBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
        const openLabel = this.cfg.mobile_aside_open_label || 'OPEN';
        const hideLabel = this.cfg.mobile_aside_hide_label || 'HIDE';
        if (this._asideToggleText) {
          this._asideToggleText.textContent = open ? hideLabel : openLabel;
        }
        if (open) {
          this._mainEl?.setAttribute('aria-hidden', 'true');
        } else {
          this._mainEl?.removeAttribute('aria-hidden');
        }

        if (mobile) {
          if (open) {
            this._bundleAsideScrollY = window.scrollY;
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            document.documentElement.classList.add('bundle-builder-mobile-aside-open');
            requestAnimationFrame(() => {
              this._syncBundleMobileOverlayTop();
              this._syncMobileAsidePortal(true);
              requestAnimationFrame(() => this._syncBundleMobileOverlayTop());
            });
          } else {
            document.documentElement.classList.remove('bundle-builder-mobile-aside-open');
            this._syncMobileAsidePortal(false);
            document.documentElement.style.removeProperty('--bundle-mobile-overlay-top');
            const y = this._bundleAsideScrollY;
            this._bundleAsideScrollY = null;
            if (y != null) {
              requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'auto' }));
            }
          }
        } else {
          document.documentElement.classList.remove('bundle-builder-mobile-aside-open');
          document.documentElement.style.removeProperty('--bundle-mobile-overlay-top');
          this._teardownMobileAsidePortal();
          if (this._bundleAsideScrollY != null) {
            const y = this._bundleAsideScrollY;
            this._bundleAsideScrollY = null;
            window.scrollTo({ top: y, behavior: 'auto' });
          }
        }
      }

      _syncBundleMobileOverlayTop() {
        const ann = document
          .querySelector('.announcement-bar-section')
          ?.closest('[id^="shopify-section"]');
        const header = document.querySelector('header-section')?.closest('[id^="shopify-section"]');

        let edge = 0;
        if (ann) {
          const r = ann.getBoundingClientRect();
          if (r.height > 0) edge = Math.max(edge, r.bottom);
        }
        if (header) {
          const r = header.getBoundingClientRect();
          if (r.height > 0) edge = Math.max(edge, r.bottom);
        }

        if (edge > 0) {
          document.documentElement.style.setProperty(
            '--bundle-mobile-overlay-top',
            `${Math.round(edge)}px`
          );
        }
      }

      _syncMobileAsidePortal(open) {
        const aside = this._asideEl;
        if (!aside || this._mql?.matches) return;

        if (!open) {
          this._teardownMobileAsidePortal();
          return;
        }

        if (aside.parentNode === document.body) return;

        this._asidePortalPlaceholder = document.createComment('bundle-builder-aside-placeholder');
        aside.parentNode.insertBefore(this._asidePortalPlaceholder, aside);

        this._mobileAsideScrim = document.createElement('div');
        this._mobileAsideScrim.className = 'bundle-builder__mobile-aside-scrim';
        this._mobileAsideScrim.setAttribute('aria-hidden', 'true');

        document.body.appendChild(this._mobileAsideScrim);
        document.body.appendChild(aside);
      }

      _teardownMobileAsidePortal() {
        const aside = this._asideEl;
        if (this._mobileAsideScrim) {
          this._mobileAsideScrim.remove();
          this._mobileAsideScrim = null;
        }
        if (
          aside &&
          this._asidePortalPlaceholder?.parentNode &&
          aside.parentNode === document.body
        ) {
          this._asidePortalPlaceholder.parentNode.insertBefore(aside, this._asidePortalPlaceholder);
          this._asidePortalPlaceholder.remove();
          this._asidePortalPlaceholder = null;
        }
      }

      _bundleCharmAddBlocked() {
        const maxItems = this.cfg.max_bundle_items || 6;
        const maxCharms = this.cfg.max_step2_charms || 5;
        const used = (this.chain ? 1 : 0) + this.charms.length;
        if (used >= maxItems) return true;
        if (this.charms.length >= maxCharms) return true;
        return false;
      }

      _notifyStateChanged() {
        refreshGridsBundleState(this);
        renderBundleSidebar(this);
        if (this._qvContext) renderQuickModalActions(this);
      }

      _setStepUi(step) {
        if (step === 1) {
          this._panels1?.classList.remove('hidden');
          this._panels1?.removeAttribute('hidden');
          this._panels2?.classList.add('hidden');
          this._panels2?.setAttribute('hidden', '');
          this._head1?.classList.remove('hidden');
          this._head1?.removeAttribute('hidden');
          this._head2?.classList.add('hidden');
          this._head2?.setAttribute('hidden', '');
        } else {
          this._panels1?.classList.add('hidden');
          this._panels1?.setAttribute('hidden', '');
          this._panels2?.classList.remove('hidden');
          this._panels2?.removeAttribute('hidden');
          this._head1?.classList.add('hidden');
          this._head1?.setAttribute('hidden', '');
          this._head2?.classList.remove('hidden');
          this._head2?.removeAttribute('hidden');
        }
      }

      _jumpToStep1() {
        closeQuickView(this);
        this.chain = null;
        this.charms = [];
        this.uiStep = 1;
        this._progressStep3AfterAtc = false;
        this.selectedVariantByProduct.clear();
        this.metalFilter = '';
        applyStep2MetalFilter(this);
        this._setStepUi(1);
        this._notifyStateChanged();
        this._renderProgress();
        this._updateATC();
        this._updateBottomBanner();
      }

      _activateTab(tab) {
        closeQuickView(this);
        const id = tab.getAttribute('data-tab-for');
        if (!id) return;
        const root = tab.closest('.bundle-builder__panels');
        if (!root) return;

        root.querySelectorAll('.bundle-builder__tab').forEach((t) => {
          const on = t === tab;
          t.classList.toggle('bundle-builder__tab--active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });

        root.querySelectorAll(':scope > .bundle-builder__tab-panel').forEach((panel) => {
          const grid = panel.querySelector(`[data-bundle-grid="${id}"]`);
          const show = !!grid;
          panel.hidden = !show;
          panel.classList.toggle('bundle-builder__tab-panel--active', show);
        });
      }

      _productsForGridKey(gridKey) {
        if (!gridKey) return [];
        const blockId = gridKey.replace(/^step[12]-/, '');
        return this.productsByBlock.get(blockId) || [];
      }

      /** Resolve product for a card by grid index (handles duplicate product IDs in one list). */
      _productForCard(card) {
        if (!card) return null;
        const grid = card.closest('[data-bundle-grid]');
        const key = grid?.getAttribute('data-bundle-grid');
        const products = this._productsForGridKey(key);
        const index = parseInt(card.getAttribute('data-card-index'), 10);
        if (!Number.isNaN(index) && index >= 0 && index < products.length) {
          return products[index];
        }
        const pid = card.getAttribute('data-product-id');
        if (!pid) return null;
        return products.find((p) => String(p.id) === String(pid)) || null;
      }

      _productById(productId) {
        const sid = String(productId);
        for (const products of this.productsByBlock.values()) {
          const found = products.find((p) => String(p.id) === sid);
          if (found) return found;
        }
        return null;
      }

      async _enrichProduct(product) {
        return enrichProduct(product);
      }

      _goToStep2() {
        closeQuickView(this);
        this._progressStep3AfterAtc = false;
        this.uiStep = 2;
        this._setStepUi(2);
        refreshGridsBundleState(this);
        this._renderProgress();
        this._scrollToStepHeading(this._head2);
      }

      _scrollToStepHeading(headingEl) {
        const target = headingEl || this;
        if (!target) return;
        requestAnimationFrame(() => {
          const cs = getComputedStyle(document.documentElement);
          const headerH = parseFloat(cs.getPropertyValue('--header-height')) || 0;
          const annH = parseFloat(cs.getPropertyValue('--announcement-bar-height')) || 0;
          const asideBar = this.querySelector('.bundle-builder__aside-head');
          const asideH = asideBar ? asideBar.getBoundingClientRect().height : 0;
          const offset = headerH + annH + asideH + 16;
          const y = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        });
      }

      _addChain(product, variantId) {
        const v = product.variants?.find((x) => x.id === variantId);
        if (!v || !variantAvailable(v)) return;
        closeQuickView(this);
        const src = variantFeaturedSrc(v, product);
        this.chain = {
          productId: product.id,
          variantId,
          title: product.title,
          variantTitle: v.title,
          price: v.price,
          image: src,
          lineId: uuid(),
        };
        this.metalFilter = detectMetalFromVariantTitle(v.title);
        applyStep2MetalFilter(this);
        this._goToStep2();
        renderBundleSidebar(this);
        this._updateATC();
        this._updateBottomBanner();
      }

      _addCharm(product, variantId) {
        const maxItems = this.cfg.max_bundle_items || 6;
        const maxCharms = this.cfg.max_step2_charms || 5;
        const used = (this.chain ? 1 : 0) + this.charms.length;
        if (used >= maxItems) return;
        if (this.charms.length >= maxCharms) return;

        const v = product.variants?.find((x) => x.id === variantId);
        if (!v || !variantAvailable(v)) return;

        closeQuickView(this);
        const src = variantFeaturedSrc(v, product);
        this.charms.push({
          productId: product.id,
          variantId,
          title: product.title,
          variantTitle: v.title,
          price: v.price,
          image: src,
          lineId: uuid(),
        });
        this._progressStep3AfterAtc = false;
        this._notifyStateChanged();
        this._updateATC();
        renderBundlePromo(this);
        this._renderProgress();
      }

      _removeCharmVariant(variantId) {
        for (let i = this.charms.length - 1; i >= 0; i--) {
          if (this.charms[i].variantId === variantId) {
            this.charms.splice(i, 1);
            break;
          }
        }
        this._progressStep3AfterAtc = false;
        this._notifyStateChanged();
        this._updateATC();
        renderBundlePromo(this);
        this._renderProgress();
      }

      _removeLine(lineId) {
        this._progressStep3AfterAtc = false;
        if (this.chain && this.chain.lineId === lineId) {
          this.chain = null;
          this.charms = [];
          this.uiStep = 1;
          this.metalFilter = '';
          applyStep2MetalFilter(this);
          this._setStepUi(1);
          this._notifyStateChanged();
          this._renderProgress();
        } else {
          const idx = this.charms.findIndex((c) => c.lineId === lineId);
          if (idx >= 0) this.charms.splice(idx, 1);
          this._notifyStateChanged();
          this._renderProgress();
        }
        renderBundleSidebar(this);
        this._updateATC();
        renderBundlePromo(this);
        this._updateBottomBanner();
      }

      _renderProgress() {
        if (!this._progressNav) return;
        const s1 = this._progressNav.querySelector('[data-progress-jump="1"]');
        const s2 = this._progressNav.querySelector('[data-progress-display="2"]');
        const s3 = this._progressNav.querySelector('[data-progress-display="3"]');
        [s1, s2, s3].forEach((el) => el?.classList.remove('is-active', 'is-complete'));

        if (this._progressStep3AfterAtc) {
          s1?.classList.add('is-complete');
          s2?.classList.add('is-complete');
          s3?.classList.add('is-active');
          return;
        }

        if (this.uiStep === 1 || !this.chain) {
          s1?.classList.add('is-active');
        } else {
          s1?.classList.add('is-complete');
          s2?.classList.add('is-active');
        }
      }

      _totalCents() {
        let t = 0;
        if (this.chain) t += this.chain.price;
        this.charms.forEach((c) => {
          t += c.price;
        });
        const minCharms = this.cfg.discount_min_charms || 2;
        const pct = this.cfg.discount_percent || 0;
        const qualifies = this.charms.length >= minCharms && pct > 0;
        if (qualifies) {
          return Math.round(t * (1 - pct / 100));
        }
        return t;
      }

      _updateATC() {
        const btn = this._atcBtn;
        if (!btn) return;
        const hasChain = !!this.chain;
        const hasCharm = this.charms.length > 0;
        const canAdd = hasChain && hasCharm;
        btn.disabled = !canAdd;
        const total = this._totalCents();
        const label = this.cfg.add_to_cart_label || 'ADD TO CART';
        const hasAnyBundleItem = hasChain || this.charms.length > 0;
        const showPriceSuffix = hasAnyBundleItem && total > 0;
        const text = showPriceSuffix
          ? `${label} | ${formatMoney(total, this.cfg.money_format)}`
          : label;
        const labelEl = btn.querySelector('.bundle-builder__atc-label');
        if (labelEl) {
          labelEl.textContent = text;
        } else {
          btn.textContent = text;
        }
      }

      async _addBundleToCart() {
        if (!this.chain || !this.charms.length) return;
        const items = buildBundleCartItems(this);
        if (!items.length) return;
        const btn = this._atcBtn;
        if (!btn || btn.hasAttribute('processing')) return;
        btn.setAttribute('processing', '');
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
        const labelEl = btn.querySelector('.bundle-builder__atc-label');
        if (labelEl) {
          labelEl.innerHTML = BUNDLE_ATC_LOADING_DOTS_HTML;
        }
        try {
          await cartAdd(items);
          this._progressStep3AfterAtc = true;
          this._renderProgress();
        } catch {
          /* optionally toast */
        } finally {
          btn.removeAttribute('processing');
          btn.removeAttribute('aria-busy');
          this._updateATC();
        }
      }
    }
  );
}
